import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Reveal } from "./Extras";
import { buildWhatsAppUrl } from "../data/content";

const FILTERS = [
    { id: "todos", label: "Todos" },
    { id: "aves", label: "Aves" },
    { id: "conejos", label: "Conejos" },
    { id: "roedores", label: "Roedores" },
];

const CATEGORY_LABEL = { aves: "Aves", conejos: "Conejos", roedores: "Roedores" };

const Products = ({ products, config, loading }) => {
    const [filter, setFilter] = useState("todos");
    const visible = filter === "todos" ? products : products.filter((p) => p.category === filter);

    return (
        <section id="productos" data-testid="products-section" className="bg-brand-cream py-12 md:py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-xl">
                        <p className="text-sm font-bold uppercase tracking-widest text-brand-magenta">Catálogo</p>
                        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl">
                            Productos LoloPets
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-brand-muted">
                            Encontralos en las principales veterinarias y pet shops del país.
                            ¿Consultas? Escríbenos por WhatsApp.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="mt-8">
                    <div
                        data-testid="category-filters"
                        className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:px-0"
                        role="tablist"
                        aria-label="Filtrar por categoría"
                    >
                        {FILTERS.map((f) => (
                            <button
                                key={f.id}
                                data-testid={`filter-pill-${f.id}`}
                                role="tab"
                                aria-selected={filter === f.id}
                                onClick={() => setFilter(f.id)}
                                className={`min-h-[44px] shrink-0 rounded-full px-6 font-display text-sm font-semibold transition-all duration-300 ${
                                    filter === f.id
                                        ? "bg-brand-magenta text-white shadow-soft"
                                        : "bg-white text-brand-purple hover:bg-brand-magenta/10"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                {loading ? (
                    <div data-testid="products-loading" className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-8">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="animate-pulse rounded-2xl bg-white p-4 shadow-soft">
                                <div className="aspect-square rounded-xl bg-brand-cream" />
                                <div className="mt-4 h-3 w-2/3 rounded bg-brand-cream" />
                                <div className="mt-2 h-3 w-1/3 rounded bg-brand-cream" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <motion.div layout className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
                        <AnimatePresence mode="popLayout">
                            {visible.map((p, i) => (
                                <motion.article
                                    layout
                                    key={p.slug}
                                    data-testid={`product-card-${p.slug}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.3) }}
                                    className="group flex flex-col rounded-2xl bg-white p-4 shadow-soft transition-shadow duration-300 hover:shadow-lift sm:p-5"
                                >
                                    <Link to={`/productos/${p.slug}`} data-testid={`product-link-${p.slug}`} className="block">
                                        <div className="overflow-hidden rounded-xl bg-black/[0.03]">
                                            <img
                                                src={p.image}
                                                alt={`${p.name} — LoloPets Costa Rica`}
                                                loading="lazy"
                                                className="aspect-square w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                    </Link>
                                    <div className="mt-4 flex flex-1 flex-col">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] font-bold uppercase tracking-widest text-brand-magenta">
                                                {p.line}
                                            </span>
                                            <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-muted">
                                                · {CATEGORY_LABEL[p.category]}
                                            </span>
                                        </div>
                                        <h3 className="mt-1.5 font-display text-sm font-semibold leading-snug text-brand-ink sm:text-base">
                                            <Link to={`/productos/${p.slug}`} className="transition-colors duration-200 hover:text-brand-magenta">
                                                {p.name}
                                            </Link>
                                        </h3>
                                        <a
                                            data-testid={`product-ask-${p.slug}`}
                                            href={buildWhatsAppUrl(config?.whatsapp, `Hola LoloPets, me interesa: ${p.name}. ¿Dónde lo puedo comprar?`)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-brand-purple transition-colors duration-200 hover:text-brand-magenta"
                                        >
                                            <MessageCircle className="h-4 w-4" /> Consultar
                                        </a>
                                    </div>
                                </motion.article>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default Products;
